import React, { Fragment, useRef, useState } from 'react';
import ReactHtmlParser from 'react-html-parser' ;
import ReactModal from 'react-modal';
import sanitizeHtml from 'sanitize-html'
import { useTranslation } from 'react-i18next';
import { QueryParams } from '../../utils/SiteTools';

import { modal } from './data/modal.json';
import * as MODALS from '../../constants/modals';
import './Modal.scss';

/**
 * 
 * @param id - Unique ID of the anchor/button element
 * @param className - Extra class names to be assigned to element
 * @param component - To which the element belongs to
 * @param linkName = Text of the anchor/button element
 * @param dataTag = String for tagging
 * @param dataModal = String for data modal type
 * @param contentId - Content ID to identify which content to display
 * @param url - value of `href` attribute in case of the anchor element
 * @param ariaLabel - value of 'aria-label' attribute for the link
 */
function Modal(props) {

	const { id, className, component, dataTag, linkName, contentId, url, dataModal, lang, hrefLang, target, ariaLabel } = props;

	ReactModal.setAppElement('#root');
	ReactModal.defaultStyles.overlay.backgroundColor = MODALS.DARK_BACKGROUND;
	ReactModal.defaultStyles.overlay.zIndex = 10;

	const modalRef = useRef();
	const { t } = useTranslation('modal');
	const [isModalOpen, setModalOpen] = useState(false);
	const [externalURI, setExternalURI] = useState(null);
	const [demoLinkTarget, setDemoLinkTarget] = useState(null);

	const MODAL_STYLES = dataModal === 'Advertising_Practices' ? MODALS.ADVERTISING_PRACTICES : dataModal === 'Exit_Lang' ? MODALS.EXIT_MODAL : MODALS.DEMO_STEPS_MODAL;
	
	const openModal = () => {
		setModalOpen(true);
		setTimeout(() => { QueryParams(); }, 150);
	}
	const closeModal = () => {
		setModalOpen(false);
	}

	const openInterstitialModal = e => {
		e.preventDefault();
		const anchor = e.target.closest("a");   // Find closest Anchor (or self)
  		if (!anchor) return;
		const hrefUri = anchor.getAttribute('href');
		const demoLinkTarget = anchor.getAttribute('target');
		setDemoLinkTarget(demoLinkTarget);
		setExternalURI(hrefUri);
		setModalOpen(true);
	}

	const APModalMarkupData = modal.advertising_practices.paragraphs;
	const APModalMarkup = APModalMarkupData.map((para, i) => t(para));
	/**
	 * This function returns a JSX element that contains the markup for the Advertising Practices modal
	 * @returns The markup is being returned as a string.
	 */
	const showAdvertisingPractices = () => {
		return (<Fragment>
			<h1 className="modal-title" id={`modalTitle_${contentId}`} dangerouslySetInnerHTML={{ __html: t(modal.advertising_practices.title) }}></h1>
				{APModalMarkup.map( (markup, indx) => (
					<p key={indx}>
						{ReactHtmlParser(markup, {
							transform: function (node, i) {
								if (node.type === 'tag' && node.name === 'a') {
									switch(node.attribs["data-modal-target"]) {
										case "exit-link":
											return (
												<span key={i}>
													<Modal url={node.attribs.href} className="t-track-link" component="a" dataModal={`Exit_Lang`} linkName={node.children[0].data} contentId="interstitial_link"/> 
												</span>
											)
										case "exit-lang": 
											return (
												<span key={i}>
													<Modal url={node.attribs.href} className="t-track-link" component="a" dataModal={`Exit_Lang`} linkName={node.children[0].data} contentId="interstitial"/>
												</span>
											)
										default:
											return (
												<span key={i}>
													<a href={node.attribs.href} className="t-track-link" dangerouslySetInnerHTML={{ __html: sanitizeHtml(t(node.children[0].data))}} target="_blank" rel="noopener noreferrer"></a>
												</span>
											)
									}
								}
							}
						})}
					</p>
				))}
		</Fragment>)
	}

	/**
	 * This function returns a modal that contains a header, subheader, copy, and two buttons. 
	 * 
	 * The header and subheader are both HTML strings that are passed in as props. 
	 * 
	 * The copy is also an HTML string that is passed in as a prop. 
	 * 
	 * The two buttons are both links. One is a blue button that contains the text "Continue" and the
	 * other is a gray button that contains the text "Cancel". 
	 * 
	 * The blue button is a link that opens the external URL in a new tab. 
	 * 
	 * The gray button is a link that closes the modal.
	 * @returns A React Fragment.
	 */
	const showInterstitialModal = (exitModalType) => {
		return (<Fragment>
			<div className="modal__content modal__body--wrapper">                
			<div id="modal__exit-es" className="modal__body modal__body--exit-es exit modal__align--left" style={{ maxWidth: '530px', display: 'block' }}>
				<h2 className="modal__title" id={`modalTitle_${contentId}`} dangerouslySetInnerHTML={{ __html: t(modal.interstitial[exitModalType].header) }}></h2>
				<div className="modal__subtitle" dangerouslySetInnerHTML={{ __html: t(modal.interstitial[exitModalType].subheader) }}></div>
					<p dangerouslySetInnerHTML={{ __html: t(modal.interstitial[exitModalType].copy) }}></p>
					<div className="modal__links align--left">
						<a className="cta cta--blue modal-exit--continue js-modal--continue t-track-custom-click" href={externalURI} rel="noopener noreferrer" target={demoLinkTarget==='' || demoLinkTarget===null ? '_blank' : demoLinkTarget} onClick={closeModal} data-bactmln="Modal-_-Click-_-Continue"><span className="cta--mobile" dangerouslySetInnerHTML={{ __html: t(modal.interstitial[exitModalType].continueMob) }}></span><span className="cta--desktop" dangerouslySetInnerHTML={{ __html: t(modal.interstitial[exitModalType].linkContinue) }}></span></a>                            
						<button className="cta cta--gray cta--cancel modal-exit--cancel js-modal--close close t-track-custom-click"  onClick={closeModal} data-bactmln="Modal-_-Click-_-Cancel"><span className="cta--mobile" dangerouslySetInnerHTML={{ __html: t(modal.interstitial[exitModalType].cancelMob) }}></span><span className="cta--desktop" dangerouslySetInnerHTML={{ __html: t(modal.interstitial[exitModalType].linkCancel) }}></span></button>                        
					</div>
			</div>
			</div>
		</Fragment>)
	}

	
	const defaultContent = () => {
		return "Nonthing to show..."
	}

/**
 * It returns the content for the modal.
 * @returns A JSX element.
 */
	const buildModalContent = () => {
		switch (contentId) {
			case 'advertising-practices':
				return showAdvertisingPractices();
				case 'interstitial':
					return showInterstitialModal("exit_lang");
				case 'interstitial_link':
					return showInterstitialModal("exit_link");
			default:
				return defaultContent();
		}
	}

	

	/* Returning a JSX element that contains the markup for the modal. */
	return (
		<Fragment>
			{
				url
					? <a ref={modalRef} id={id} href={url} target={target} lang={lang} hrefLang={hrefLang} data-component={component} className={`interstitial ${className}`} dangerouslySetInnerHTML={{ __html: linkName }} onClick={openInterstitialModal} data-bactmln={dataTag} data-modal={dataModal} rel="noopener noreferrer"/>
					: <button ref={modalRef} id={id} aria-label={ariaLabel} data-component={component} data-bactmln={dataTag} className={`btn btn-modal-open ${className}`} onClick={openModal} dangerouslySetInnerHTML={{ __html: linkName }} data-modal={dataModal}></button>
			}
			<ReactModal className={dataModal} isOpen={isModalOpen} onRequestClose={closeModal} shouldCloseOnOverlayClick={MODALS.DEFAULT_CLOSE_BEHAVIOR} style={MODAL_STYLES} aria={{ labelledby: `modalTitle_${contentId}` }}>
				<div className={`modal-wrapper ${className}`}>
					<button className="btn-close t-track-custom" data-bactmln="Modal-_-Click-_-Close" autoFocus aria-label={t(modal.btnCloseText)} onClick={closeModal}>X</button>

					{buildModalContent()}
				</div>
			</ReactModal>
		</Fragment>
	)
};

export default Modal;